import { create } from "zustand";

const DEFAULT_USERS = [
  {
    id: "u_001",
    name: "Sarah Jenkins",
    email: "sarah.j@apexholdings.com",
    company: "Apex Holdings",
    role: "investor",
    registeredAt: "2026-05-12T08:30:00Z",
    status: "Active",
  },
  {
    id: "u_002",
    name: "Michael Chang",
    email: "mchang@crestviewcap.com",
    company: "Crestview Capital",
    role: "investor",
    registeredAt: "2026-05-20T14:45:00Z",
    status: "Active",
  },
  {
    id: "u_003",
    name: "David Ross",
    email: "dross@valleyrep.com",
    company: "Valley Real Estate Partners",
    role: "team_member",
    registeredAt: "2026-06-01T10:15:00Z",
    status: "Active",
  },
  {
    id: "u_004",
    name: "Elena Rostova",
    email: "elena@rostovadocs.com",
    company: "Rostova Development",
    role: "investor",
    registeredAt: "2026-06-04T09:00:00Z",
    status: "Suspended",
  },
  {
    id: "u_005",
    name: "Admin User",
    email: "admin@acquireos.com",
    company: "AcquireOS Team",
    role: "admin",
    registeredAt: "2026-01-10T12:00:00Z",
    status: "Active",
  },
];

const DEFAULT_DEALS = [
  {
    id: "ad_001",
    name: "Apex Multifamily Portfolio",
    userEmail: "sarah.j@apexholdings.com",
    price: 8500000,
    units: 84,
    capRate: 5.6,
    stage: "Underwriting",
    status: "Pending Validation",
    submittedAt: "2026-06-08T11:20:00Z",
  },
  {
    id: "ad_002",
    name: "Crestview Retail Plaza",
    userEmail: "mchang@crestviewcap.com",
    price: 4200000,
    units: 24,
    capRate: 6.2,
    stage: "LOI Sent",
    status: "Validated",
    submittedAt: "2026-06-05T16:40:00Z",
  },
  {
    id: "ad_003",
    name: "Valley Office Complex",
    userEmail: "dross@valleyrep.com",
    price: 12800000,
    units: 45,
    capRate: 7.1,
    stage: "Under Contract",
    status: "Pending Validation",
    submittedAt: "2026-06-09T14:10:00Z",
  },
];

const DEFAULT_LOGS = [
  {
    id: "log_001",
    timestamp: "2026-06-10T09:12:45Z",
    userEmail: "sarah.j@apexholdings.com",
    component: "Module 4 Underwriting Engine",
    message: "Underwriting calculation divide-by-zero on expense ratio check",
    severity: "High",
    status: "Unresolved",
  },
];

// ── Helpers for local-user sync ──────────────────────────────────────────────

/** Read all users registered offline (localStorage fallback). */
function getLocalUsers() {
  try {
    return JSON.parse(localStorage.getItem('acquireos_local_users') || '[]');
  } catch {
    return [];
  }
}

/** Write the remaining (un-synced) users back to localStorage. */
function setLocalUsers(users) {
  localStorage.setItem('acquireos_local_users', JSON.stringify(users));
}

/**
 * Merge a list of locally-stored users into the server list without duplicates.
 * Matching is done by email (case-insensitive).
 */
function mergeLocalIntoServer(serverUsers, localUsers) {
  const emails = new Set(serverUsers.map(u => u.email.toLowerCase()));
  const extra = localUsers.filter(u => !emails.has(u.email.toLowerCase()));
  return extra.length ? [...serverUsers, ...extra] : serverUsers;
}

/**
 * Helper to fetch a resource with automatic retries on transient errors (like Heroku 503/502/504 cold starts).
 */
const fetchWithRetry = async (url, options = {}, retries = 3, delay = 2000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, options);
      if (res.status === 503 || res.status === 502 || res.status === 504) {
        if (i < retries - 1) {
          console.warn(`[adminStore] Fetch to ${url} returned ${res.status}. Retrying in ${delay}ms... (Attempt ${i + 1}/${retries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }
      return res;
    } catch (err) {
      if (i < retries - 1) {
        console.warn(`[adminStore] Fetch to ${url} failed: ${err.message}. Retrying in ${delay}ms... (Attempt ${i + 1}/${retries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw err;
    }
  }
  return fetch(url, options);
};

// ─────────────────────────────────────────────────────────────────────────────

const useAdminStore = create((set, get) => ({
  users: DEFAULT_USERS,
  deals: DEFAULT_DEALS,
  logs: DEFAULT_LOGS,
  isLoading: false,

  /**
   * Push any users that were registered offline (localStorage) to the backend.
   * Removes successfully-synced entries from localStorage.
   * Returns true if the server was reachable.
   */
  syncLocalUsers: async () => {
    const localUsers = getLocalUsers();
    if (!localUsers.length) return true;

    const remaining = [];
    let serverReachable = false;

    for (const user of localUsers) {
      try {
        const res = await fetch('/api/admin/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...user, status: user.status || 'Pending' }),
        });
        serverReachable = true;
        if (!res.ok) remaining.push(user); // server error — keep for retry
      } catch {
        remaining.push(user); // network error — keep for retry
      }
    }

    setLocalUsers(remaining);
    return serverReachable;
  },

  // Fetch all active records from the backend database
  fetchAdminData: async () => {
    set({ isLoading: true });
    try {
      // Push any offline-registered users to the DB before reading
      await get().syncLocalUsers();

      // Fetch sequentially to prevent Heroku router from timing out concurrent requests when waking up the backend dyno
      const usersRes = await fetchWithRetry("/api/admin/users");
      if (!usersRes.ok) {
        throw new Error(`Server returned error status: Users(${usersRes.status})`);
      }
      const uData = await usersRes.json();

      const dealsRes = await fetchWithRetry("/api/admin/deals");
      if (!dealsRes.ok) {
        throw new Error(`Server returned error status: Deals(${dealsRes.status})`);
      }
      const dData = await dealsRes.json();

      const logsRes = await fetchWithRetry("/api/admin/logs");
      if (!logsRes.ok) {
        throw new Error(`Server returned error status: Logs(${logsRes.status})`);
      }
      const lData = await logsRes.json();

      // Merge any remaining local users not yet reflected on the server
      const remainingLocal = getLocalUsers();
      const serverUsers = uData.users && uData.users.length ? uData.users : DEFAULT_USERS;
      const mergedUsers = mergeLocalIntoServer(serverUsers, remainingLocal);

      set({
        users: mergedUsers,
        deals: dData.deals && dData.deals.length ? dData.deals : DEFAULT_DEALS,
        logs: lData.logs && lData.logs.length ? lData.logs : DEFAULT_LOGS,
        isLoading: false,
      });
    } catch (e) {
      console.error("Failed to fetch database admin data:", e);
      
      // If we already have fetched users/deals previously, preserve them rather than resetting to defaults!
      const currentUsers = get().users;
      const hasRealData = currentUsers && currentUsers.length > DEFAULT_USERS.length;
      
      if (hasRealData) {
        set({ isLoading: false });
      } else {
        // Fallback to merged defaults only if no real data was ever successfully loaded
        const localUsers = getLocalUsers();
        set({
          users: mergeLocalIntoServer(DEFAULT_USERS, localUsers),
          isLoading: false,
        });
      }
    }
  },

  updateUserStatus: async (userId, status) => {
    try {
      await fetch(`/api/admin/users/${userId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      // Fallback local update
      set((state) => ({
        users: state.users.map((u) => (u.id === userId ? { ...u, status } : u)),
      }));
    }
  },

  deleteUser: async (userId) => {
    try {
      await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
      });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      // Fallback local update
      set((state) => ({
        users: state.users.filter((u) => u.id !== userId),
      }));
    }
  },

  updateUserRole: async (userId, role) => {
    try {
      await fetch(`/api/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      set((state) => ({
        users: state.users.map((u) => (u.id === userId ? { ...u, role } : u)),
      }));
    }
  },

  validateDeal: async (dealId, status) => {
    try {
      await fetch(`/api/admin/deals/${dealId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      set((state) => ({
        deals: state.deals.map((d) => (d.id === dealId ? { ...d, status } : d)),
      }));
    }
  },

  deleteDeal: async (dealId) => {
    try {
      await fetch(`/api/admin/deals/${dealId}`, {
        method: "DELETE",
      });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      set((state) => ({
        deals: state.deals.filter((d) => d.id !== dealId),
      }));
    }
  },

  resolveLog: async (logId) => {
    try {
      await fetch(`/api/admin/logs/${logId}/resolve`, {
        method: "PUT",
      });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      set((state) => ({
        logs: state.logs.map((l) => (l.id === logId ? { ...l, status: "Resolved" } : l)),
      }));
    }
  },

  simulateError: async (userEmail, component, message, severity) => {
    try {
      const payload = {
        userEmail: userEmail || "anonymous@acquireos.com",
        component: component || "General System Engine",
        message: message || "Simulated Exception occurred",
        severity: severity || "Medium",
        status: "Unresolved",
      };
      await fetch("/api/admin/logs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      set((state) => ({
        logs: [
          {
            id: `log_${Date.now()}`,
            timestamp: new Date().toISOString(),
            userEmail,
            component,
            message,
            severity,
            status: "Unresolved",
          },
          ...state.logs,
        ],
      }));
    }
  },

  resetAdminData: async () => {
    try {
      await fetch("/api/admin/reset", { method: "POST" });
      await get().fetchAdminData();
    } catch (e) {
      console.error(e);
      set({
        users: DEFAULT_USERS,
        deals: DEFAULT_DEALS,
        logs: DEFAULT_LOGS,
      });
    }
  },
}));

export default useAdminStore;
